import { BulletList } from '@/components/bulletList';
import { GadmClassification } from '@/components/classification';
import { ConceptValue } from '@/components/conceptValue';
import { HelpLine } from '@/components/helpText';
import { GeoJsonMap } from '@/components/maps/geojsonMap';
import { generatePointGeoJson } from '@/components/maps/geojsonMap/generatePointGeoJson';
import Properties, { Property } from '@/components/properties';
import { RenderIfChildren } from '@/components/renderIfChildren';
import { StaticRenderSuspence } from '@/components/staticRenderSuspence';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/largeCard';
import { useConfig } from '@/config/config';
import { OccurrenceQuery, SlowOccurrenceKeyQuery, Term } from '@/gql/graphql';
import { DynamicLink } from '@/reactRouterPlugins';
import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import wellknown from 'wellknown';
import {
  BasicField,
  DepthField,
  ElevationField,
  EnumField,
  HtmlField,
  LicenseField,
  PlainTextField,
  VerbatimTextField,
} from '../properties';
import {
  CollectionKey,
  DatasetKey,
  DynamicProperties,
  IdentifiedById,
  InstitutionKey,
  LocalContexts,
  OccurrenceSearchField,
  RecordedById,
} from './customValues';
import {
  Amplification,
  ChronometricAge,
  Cloning,
  DNADerivedData,
  EolReference,
  ExtendedMeasurementOrFact,
  GelImage,
  GermplasmAccession,
  GermplasmMeasurementScore,
  GermplasmMeasurementTrait,
  GermplasmMeasurementTrial,
  IdentificationHistory,
  Identifier,
  Loan,
  MaterialSampleExt,
  MeasurementOrFact,
  Permit,
  Preparation,
  Preservation,
  Reference,
  ResourceRelationship,
} from './extensions';
import { Media } from './media';
import { TaxonInterpretationCard, ChecklistNoMatchCard } from './TaxonInterpretationCard';
import { CardDescription } from '@/components/ui/smallCard';
import { MediaSummary } from './MediaSummary';
import { cn } from '@/utils/shadcn';
import TestSiteAlert from '@/components/TestSiteAlert';

export function Groups({
  occurrence,
  slowOccurrence,
  showAll,
  updateToc,
  termMap,
}: {
  occurrence: OccurrenceQuery['occurrence'];
  slowOccurrence: SlowOccurrenceKeyQuery['occurrence'];
  showAll: boolean;
  updateToc: (id: string, visible: boolean) => void;
  termMap: { [key: string]: Term };
}) {
  if (!occurrence) return null;
  return (
    <div style={{ wordBreak: 'break-word' }}>
      <MediaSummary occurrence={occurrence} termMap={termMap} />
      {/*<SequenceTeaser       {...{ updateToc, showAll, termMap, occurrence, setActiveImage }} />*/}
      {/* <Summary {...{ updateToc, showAll, termMap, occurrence }} /> */}

      {/* <Provenance {...{ updateToc, showAll, termMap, occurrence }} /> */}

      <Record {...{ showAll, termMap, occurrence, slowOccurrence, updateToc }} />
      <Taxon {...{ updateToc, showAll, termMap, occurrence }} />
      <Identification {...{ updateToc, showAll, termMap, occurrence }} />
      <Location {...{ updateToc, showAll, termMap, occurrence }} />
      <GeologicalContext {...{ updateToc, showAll, termMap, occurrence }} />
      <Occurrence {...{ updateToc, showAll, termMap, occurrence }} />
      <Event {...{ updateToc, showAll, termMap, occurrence }} />
      <Organism {...{ updateToc, showAll, termMap, occurrence }} />
      <MaterialSample {...{ updateToc, showAll, termMap, occurrence }} />
      <Other {...{ updateToc, showAll, termMap, occurrence }} />

      <Media occurrence={occurrence} termMap={termMap} updateToc={updateToc} />

      <Preparation {...{ updateToc, showAll, termMap, occurrence }} />
      <ResourceRelationship {...{ updateToc, showAll, termMap, occurrence }} />
      <Amplification {...{ updateToc, showAll, termMap, occurrence }} />
      <Permit {...{ updateToc, showAll, termMap, occurrence }} />
      <Loan {...{ updateToc, showAll, termMap, occurrence }} />
      <Preservation {...{ updateToc, showAll, termMap, occurrence }} />
      <MaterialSampleExt {...{ updateToc, showAll, termMap, occurrence }} />
      <DNADerivedData {...{ updateToc, showAll, termMap, occurrence }} />
      <Cloning {...{ updateToc, showAll, termMap, occurrence }} />
      <GelImage {...{ updateToc, showAll, termMap, occurrence }} />
      <Reference {...{ updateToc, showAll, termMap, occurrence }} />
      <EolReference {...{ updateToc, showAll, termMap, occurrence }} />
      <GermplasmAccession {...{ updateToc, showAll, termMap, occurrence }} />
      <GermplasmMeasurementScore {...{ updateToc, showAll, termMap, occurrence }} />
      <GermplasmMeasurementTrait {...{ updateToc, showAll, termMap, occurrence }} />
      <GermplasmMeasurementTrial {...{ updateToc, showAll, termMap, occurrence }} />
      <IdentificationHistory {...{ updateToc, showAll, termMap, occurrence }} />
      <Identifier {...{ updateToc, showAll, termMap, occurrence }} />
      <MeasurementOrFact {...{ updateToc, showAll, termMap, occurrence }} />
      <ExtendedMeasurementOrFact {...{ updateToc, showAll, termMap, occurrence }} />
      <ChronometricAge {...{ updateToc, showAll, termMap, occurrence }} />
      {/* <Audubon              {...{ updateToc, showAll, termMap, occurrence }} /> */}

      <Issues {...{ occurrence }} />
      <Citation {...{ updateToc, showAll, termMap, occurrence }} />
      <Debug {...{ updateToc, showAll, termMap, occurrence }} />
    </div>
  );
}

export function Group({
  label,
  defaultMessage,
  children,
  description,
  id,
  className,
  titleExtra,
  ...props
}: {
  label: string;
  defaultMessage?: string;
  id: string;
  description?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  // Optional content rendered next to the group title (e.g. issue/flag tags for the block).
  titleExtra?: React.ReactNode;
}) {
  return (
    <Card className={cn('g-mb-4', className)} id={id}>
      <CardHeader>
        {titleExtra ? (
          <div className="g-flex g-items-center g-justify-between g-gap-2 g-flex-wrap">
            <CardTitle>
              <FormattedMessage id={label} defaultMessage={defaultMessage} />
            </CardTitle>
            <div className="g-flex-none">{titleExtra}</div>
          </div>
        ) : (
          <CardTitle>
            <FormattedMessage id={label} defaultMessage={defaultMessage} />
          </CardTitle>
        )}
        {description && <CardDescription dir="auto">{description}</CardDescription>}
      </CardHeader>
      <CardContent {...props}>{children}</CardContent>
    </Card>
  );
}

function PropGroup({
  label,
  id,
  children,
}: {
  label: string;
  id: string;
  children: React.ReactNode;
}) {
  return (
    <Group label={label} id={id}>
      <Properties breakpoint={800} className="[&>dt]:g-w-52">
        {children}
      </Properties>
    </Group>
  );
}

// function Provenance({
//   showAll,
//   termMap,
//   occurrence,
// }: {
//   showAll: boolean;
//   termMap: any;
//   occurrence: any;
// }) {
//   return (
//     <Card className="g-mb-4 g-bg-slate-300 g-text-slate-600">
//       <div className="g-py-4 g-px-4 md:g-px-8">
//         This record is part of the dataset{' '}
//         <span className="g-underline">
//           <DynamicLink
//             to={`/dataset/${occurrence.datasetKey}`}
//             pageId="datasetKey"
//             variables={{ key: occurrence.datasetKey }}
//             className="g-text-inherit"
//           >
//             {occurrence.datasetTitle}
//           </DynamicLink>
//         </span>
//         .
//       </div>
//     </Card>
//   );
// }

function Record({
  showAll,
  termMap,
  occurrence,
  slowOccurrence,
}: {
  showAll: boolean;
  termMap: any;
  occurrence: OccurrenceQuery['occurrence'];
  slowOccurrence: SlowOccurrenceKeyQuery['occurrence'];
}) {
  // no reason to test this, this group is always present since basisOfRecord is always present
  return (
    <PropGroup label="occurrenceDetails.groups.record" id="record">
      <InstitutionKey {...{ occurrence, slowOccurrence }} />
      <CollectionKey {...{ occurrence, slowOccurrence }} />
      <DatasetKey {...{ occurrence }} />

      <EnumField
        term={termMap.basisOfRecord}
        showDetails={false}
        getEnum={(value) => `enums.basisOfRecord.${value}`}
      />
      <PlainTextField term={termMap.informationWithheld} showDetails={showAll} />
      <PlainTextField term={termMap.dataGeneralizations} showDetails={showAll} />
      <LocalContexts localContexts={slowOccurrence?.localContexts} />
    </PropGroup>
  );
}

function Taxon({
  showAll,
  termMap,
  occurrence,
}: {
  showAll: boolean;
  termMap: any;
  occurrence: OccurrenceQuery['occurrence'];
  updateToc: (id: string, visible: boolean) => void;
}) {
  const config = useConfig();
  const enabledChecklists = config.availableChecklistKeys || [];
  // filter occurrence.classifications to only show those in enabledChecklists. Use the ordering of the enabledChecklists
  // to order the classifications
  const filteredClassifications = occurrence?.classifications
    .filter((classification) => enabledChecklists.includes(classification?.checklistKey))
    .sort((a, b) => {
      return (
        enabledChecklists.indexOf(a?.checklistKey) - enabledChecklists.indexOf(b?.checklistKey)
      );
    });
  return (
    <>
      <Group label="occurrenceDetails.groups.taxon" id="taxon">
        <Properties breakpoint={800} className="[&>dt]:g-w-52">
          <VerbatimTextField term={termMap.scientificName} />
          <HtmlField term={termMap.taxonID} showDetails={showAll} hideIssues hideRemarks />
          <HtmlField term={termMap.scientificNameID} showDetails={showAll} hideIssues hideRemarks />
          <HtmlField
            term={termMap.acceptedNameUsageID}
            showDetails={showAll}
            hideIssues
            hideRemarks
          />
          <HtmlField
            term={termMap.parentNameUsageID}
            showDetails={showAll}
            hideIssues
            hideRemarks
          />
          <HtmlField
            term={termMap.originalNameUsageID}
            showDetails={showAll}
            hideIssues
            hideRemarks
          />
          <HtmlField
            term={termMap.nameAccordingToID}
            showDetails={showAll}
            hideIssues
            hideRemarks
          />
          <HtmlField
            term={termMap.namePublishedInID}
            showDetails={showAll}
            hideIssues
            hideRemarks
          />
          <VerbatimTextField term={termMap.taxonConceptID} />
          <VerbatimTextField term={termMap.acceptedNameUsage} />
          <VerbatimTextField term={termMap.parentNameUsage} />
          <VerbatimTextField term={termMap.originalNameUsage} />
          <VerbatimTextField term={termMap.nameAccordingTo} />
          <VerbatimTextField term={termMap.namePublishedIn} />
          <VerbatimTextField term={termMap.namePublishedInYear} />
          <VerbatimTextField term={termMap.higherClassification} />
          <VerbatimTextField term={termMap.kingdom} />
          <VerbatimTextField term={termMap.phylum} />
          <VerbatimTextField term={termMap.class} />
          <VerbatimTextField term={termMap.order} />
          <VerbatimTextField term={termMap.family} />
          <VerbatimTextField term={termMap.genus} />
          <VerbatimTextField term={termMap.subgenus} />
          <VerbatimTextField term={termMap.specificEpithet} />
          <VerbatimTextField term={termMap.infraspecificEpithet} />
          <VerbatimTextField term={termMap.taxonRank} />
          <VerbatimTextField term={termMap.verbatimTaxonRank} />
          <VerbatimTextField term={termMap.vernacularName} />
          <VerbatimTextField term={termMap.nomenclaturalCode} />
          <VerbatimTextField term={termMap.nomenclaturalStatus} />
          <HtmlField term={termMap.taxonRemarks} showDetails={showAll} />
          <VerbatimTextField term={termMap.scientificNameAuthorship} />
          <VerbatimTextField term={termMap.taxonomicStatus} />
        </Properties>

        <div className="g-mt-4 g-border-t g-border-slate-300 g-pt-4">
          <div className="g-mb-2">
            <HelpLine id={'taxonomic-interpretations'} icon />
          </div>
          {enabledChecklists.map((checklistKey) => {
            const classification = filteredClassifications?.find(
              (c) => c.checklistKey === checklistKey
            );
            if (classification) {
              return <TaxonInterpretationCard key={checklistKey} classification={classification} />;
            }
            return <ChecklistNoMatchCard key={checklistKey} checklistKey={checklistKey} />;
          })}
        </div>
      </Group>
    </>
  );
}

function Location({
  showAll,
  termMap,
  occurrence,
}: {
  showAll: boolean;
  termMap: any;
  occurrence: OccurrenceQuery['occurrence'];
}) {
  const geoJson2 = generatePointGeoJson({
    lat: occurrence?.coordinates?.lat,
    lon: occurrence?.coordinates?.lon,
  });
  const wkt = wellknown.parse(termMap?.footprintWKT?.value || '');
  const invalidWkt = termMap?.footprintWKT?.value && termMap?.footprintWKT?.value !== '' && !wkt;
  return (
    <Card className="g-mb-4" id="location">
      <CardHeader>
        <CardTitle>
          <FormattedMessage id="occurrenceDetails.groups.location" />
        </CardTitle>
      </CardHeader>
      <CardContent className="g-w-full">
        {!!occurrence?.coordinates?.lon && !!occurrence?.coordinates?.lat && (
          <div className="g-mb-4 g-min-w-64">
            <StaticRenderSuspence fallback={<div>Loading map...</div>}>
              {/* <GeoJsonMap geoJson={geoJson} className="g-w-full g-rounded g-overflow-hidden" /> */}
              <GeoJsonMap
                geoJson={
                  wkt
                    ? {
                        type: 'FeatureCollection',
                        features: [
                          {
                            type: 'Feature',
                            geometry: wkt ?? geoJson2,
                            properties: {},
                          },
                          geoJson2,
                        ],
                      }
                    : geoJson2
                }
                className="g-w-full g-rounded g-overflow-hidden"
                initialCenter={[occurrence.coordinates.lon, occurrence.coordinates.lat]}
                initialZoom={1}
                rasterStyle="gbif-natural"
              />
            </StaticRenderSuspence>
            {invalidWkt && (
              <div className="g-bg-red-500 g-p-2 g-text-sm g-text-white g-mt-2 g-rounded">
                Invalid WKT
              </div>
            )}
          </div>
        )}
        <div>
          <Properties breakpoint={800} className="[&>dt]:g-w-52">
            <PlainTextField term={termMap.locationID} showDetails={showAll} />
            <PlainTextField term={termMap.higherGeographyID} showDetails={showAll} />
            <PlainTextField term={termMap.higherGeography} showDetails={showAll} />

            <EnumField
              term={termMap.continent}
              showDetails={showAll}
              getEnum={(value) => `enums.continent.${value}`}
            />
            <PlainTextField term={termMap.country} showDetails={showAll} />
            <PlainTextField term={termMap.countryCode} showDetails={showAll} />

            <PlainTextField term={termMap.waterBody} showDetails={showAll} />
            <PlainTextField term={termMap.islandGroup} showDetails={showAll} />
            <PlainTextField term={termMap.island} showDetails={showAll} />
            <PlainTextField term={termMap.stateProvince} showDetails={showAll} />
            <PlainTextField term={termMap.county} showDetails={showAll} />
            <PlainTextField term={termMap.municipality} showDetails={showAll} />

            <PlainTextField term={termMap.locality} showDetails={showAll} />
            <PlainTextField term={termMap.verbatimLocality} showDetails={showAll} />

            <PlainTextField
              term={termMap.minimumDistanceAboveSurfaceInMeters}
              showDetails={showAll}
            />
            <PlainTextField
              term={termMap.maximumDistanceAboveSurfaceInMeters}
              showDetails={showAll}
            />
            <PlainTextField term={termMap.locationAccordingTo} showDetails={showAll} />
            <PlainTextField term={termMap.locationRemarks} showDetails={showAll} />

            <PlainTextField term={termMap.decimalLatitude} showDetails={showAll} />
            <PlainTextField term={termMap.decimalLongitude} showDetails={showAll} />
            <PlainTextField term={termMap.coordinateUncertaintyInMeters} showDetails={showAll} />
            <PlainTextField term={termMap.coordinatePrecision} showDetails={showAll} />
            <PlainTextField term={termMap.pointRadiusSpatialFit} showDetails={showAll} />
            <PlainTextField term={termMap.footprintWKT} showDetails={showAll} />
            <PlainTextField term={termMap.footprintSRS} showDetails={showAll} />
            <PlainTextField term={termMap.footprintSpatialFit} showDetails={showAll} />
            <PlainTextField term={termMap.verbatimCoordinateSystem} showDetails={showAll} />
            <PlainTextField term={termMap.verbatimSRS} showDetails={showAll} />

            <PlainTextField term={termMap.georeferencedBy} showDetails={showAll} />
            <PlainTextField term={termMap.georeferencedDate} showDetails={showAll} />
            <HtmlField term={termMap.georeferenceProtocol} showDetails={showAll} />
            <HtmlField term={termMap.georeferenceSources} showDetails={showAll} />
            <HtmlField term={termMap.georeferenceRemarks} showDetails={showAll} />

            <ElevationField
              elevationTerm={termMap.elevation}
              elevationAccuracyTerm={termMap.elevationAccuracy}
              minimumElevationTerm={termMap.minimumElevationInMeters}
              maximumElevationTerm={termMap.maximumElevationInMeters}
              showDetails={showAll}
            />
            <PlainTextField term={termMap.verbatimElevation} showDetails={showAll} />

            <DepthField
              depthTerm={termMap.depth}
              depthAccuracyTerm={termMap.depthAccuracy}
              minimumDepthTerm={termMap.minimumDepthInMeters}
              maximumDepthTerm={termMap.maximumDepthInMeters}
              showDetails={showAll}
            />
            <PlainTextField term={termMap.verbatimDepth} showDetails={showAll} />

            <PlainTextField term={termMap.geodeticDatum} showDetails={showAll} />
            <PlainTextField term={termMap.verbatimCoordinates} showDetails={showAll} />
            <PlainTextField term={termMap.verbatimLatitude} showDetails={showAll} />
            <PlainTextField term={termMap.verbatimLongitude} showDetails={showAll} />

            {occurrence?.gadm?.level0 && (
              <BasicField label="occurrenceFieldNames.gadmClassification">
                <GadmClassification gadm={occurrence.gadm} />
              </BasicField>
            )}
          </Properties>
        </div>
      </CardContent>
    </Card>
  );
}

function Occurrence({
  showAll,
  termMap,
  occurrence,
}: {
  showAll: boolean;
  termMap: any;
  occurrence: any;
}) {
  // no reason to test this, this group is always present since occurrence ID is required
  return (
    <PropGroup label="occurrenceDetails.groups.occurrence" id="occurrence">
      <HtmlField term={termMap.occurrenceID} showDetails={showAll} />
      <PlainTextField term={termMap.catalogNumber} showDetails={showAll} />
      <PlainTextField term={termMap.recordNumber} showDetails={showAll} />
      <PlainTextField term={termMap.recordedBy} showDetails={showAll} />
      <RecordedById {...{ showAll, termMap, occurrence }} />
      <PlainTextField term={termMap.individualCount} showDetails={showAll} />
      <PlainTextField term={termMap.organismQuantity} showDetails={showAll} />
      <PlainTextField term={termMap.organismQuantityType} showDetails={showAll} />
      {termMap?.sex?.value && (
        <BasicField label="occurrenceFieldNames.sex">
          {termMap?.sex?.value && <ConceptValue vocabulary="Sex" name={termMap?.sex?.value} />}
        </BasicField>
      )}
      <PlainTextField term={termMap.lifeStage} showDetails={showAll} />
      <PlainTextField term={termMap.reproductiveCondition} showDetails={showAll} />
      <PlainTextField term={termMap.behavior} showDetails={showAll} />
      <PlainTextField term={termMap.establishmentMeans} showDetails={showAll} />
      <EnumField
        term={termMap.occurrenceStatus}
        showDetails={showAll}
        getEnum={(value) => `enums.occurrenceStatus.${value}`}
      />
      <PlainTextField term={termMap.georeferenceVerificationStatus} showDetails={showAll} />
      <PlainTextField term={termMap.preparations} showDetails={showAll} />
      <PlainTextField term={termMap.disposition} showDetails={showAll} />
      <HtmlField term={termMap.associatedReferences} showDetails={showAll} />
      <HtmlField term={termMap.associatedSequences} showDetails={showAll} />
      <HtmlField term={termMap.associatedTaxa} showDetails={showAll} />
      <PlainTextField term={termMap.otherCatalogNumbers} showDetails={showAll} />
      <PlainTextField term={termMap.occurrenceRemarks} showDetails={showAll} />
      <HtmlField term={termMap.associatedMedia} showDetails={true} />
    </PropGroup>
  );
}

function Event({
  showAll,
  termMap,
  occurrence,
  updateToc = () => {},
}: {
  showAll: boolean;
  termMap: any;
  occurrence: any;
  updateToc: (id: string, visible: boolean) => void;
}) {
  const sectionName = 'event';
  const [visible, setVisible] = useState<boolean | undefined>();
  useEffect(() => {
    if (typeof visible === 'boolean') updateToc(sectionName, visible);
  }, [visible]);

  return (
    <RenderIfChildren
      as={PropGroup}
      label="occurrenceDetails.groups.event"
      id="event"
      onChange={(newState) => {
        if (newState !== visible) setVisible(newState);
      }}
    >
      <OccurrenceSearchField
        term={termMap.eventID}
        showDetails={showAll}
        filterKey="eventId"
        filterValue={termMap?.eventID?.value}
        occurrence={occurrence}
      />
      <OccurrenceSearchField
        term={termMap.parentEventID}
        showDetails={showAll}
        filterKey="parentEventId"
        filterValue={termMap?.parentEventID?.value}
        occurrence={occurrence}
      />
      <PlainTextField term={termMap.eventDate} showDetails={showAll} />
      <PlainTextField term={termMap.eventTime} showDetails={showAll} />
      <PlainTextField term={termMap.eventType} showDetails={true} />
      {showAll && <PlainTextField term={termMap.startDayOfYear} showDetails={showAll} />}
      {showAll && <PlainTextField term={termMap.endDayOfYear} showDetails={showAll} />}
      {showAll && (
        <BasicField label="occurrenceFieldNames.year">
          {termMap?.year?.value && <span>{termMap?.year.value}</span>}
        </BasicField>
      )}
      {showAll && <PlainTextField term={termMap.month} showDetails={showAll} />}
      {showAll && <PlainTextField term={termMap.day} showDetails={showAll} />}
      <PlainTextField term={termMap.verbatimEventDate} showDetails={showAll} />
      <PlainTextField term={termMap.habitat} showDetails={showAll} />
      <HtmlField term={termMap.samplingProtocol} showDetails={showAll} />
      <PlainTextField term={termMap.samplingEffort} showDetails={showAll} />
      <PlainTextField term={termMap.sampleSizeValue} showDetails={showAll} />
      <PlainTextField term={termMap.sampleSizeUnit} showDetails={showAll} />
      {/* <SampleSize     {...{ termMap, occurrence }} /> */}
      <PlainTextField term={termMap.fieldNotes} showDetails={showAll} />
      <PlainTextField term={termMap.eventRemarks} showDetails={showAll} />
    </RenderIfChildren>
  );
}

function Organism({
  showAll,
  termMap,
  occurrence,
  updateToc = () => {},
}: {
  showAll: boolean;
  termMap: any;
  occurrence: any;
  updateToc: (id: string, visible: boolean) => void;
}) {
  const sectionName = 'organism';
  const [visible, setVisible] = useState<boolean | undefined>();
  useEffect(() => {
    if (typeof visible === 'boolean') updateToc(sectionName, visible);
  }, [visible]);

  return (
    <RenderIfChildren
      as={PropGroup}
      label="occurrenceDetails.groups.organism"
      id="organism"
      onChange={(newState) => {
        if (newState !== visible) setVisible(newState);
      }}
    >
      <OccurrenceSearchField
        term={termMap.organismID}
        showDetails={showAll}
        filterKey="organismId"
        filterValue={termMap?.organismID?.value}
        occurrence={occurrence}
      />
      <PlainTextField term={termMap.organismName} showDetails={showAll} />
      <PlainTextField term={termMap.organismScope} showDetails={showAll} />
      <PlainTextField term={termMap.associatedOccurrences} showDetails={showAll} />
      <HtmlField term={termMap.associatedOrganisms} showDetails={showAll} />
      <PlainTextField term={termMap.previousIdentifications} showDetails={showAll} />
      <PlainTextField term={termMap.organismRemarks} showDetails={showAll} />
    </RenderIfChildren>
  );
}

function MaterialSample({
  showAll,
  termMap,
  updateToc = () => {},
}: {
  showAll: boolean;
  termMap: any;
  occurrence: any;
  updateToc: (id: string, visible: boolean) => void;
}) {
  const sectionName = 'material-sample';
  const [visible, setVisible] = useState<boolean | undefined>();
  useEffect(() => {
    if (typeof visible === 'boolean') updateToc(sectionName, visible);
  }, [visible]);

  return (
    <RenderIfChildren
      as={PropGroup}
      label="occurrenceDetails.groups.materialSample"
      id="material-sample"
      onChange={(newState) => {
        if (newState !== visible) setVisible(newState);
      }}
    >
      <PlainTextField term={termMap.materialSampleID} showDetails={showAll} />
    </RenderIfChildren>
  );
}

function GeologicalContext({
  showAll,
  termMap,
  updateToc = () => {},
}: {
  showAll: boolean;
  termMap: any;
  occurrence: any;
  updateToc: (id: string, visible: boolean) => void;
}) {
  const sectionName = 'geological-context';
  const [visible, setVisible] = useState<boolean | undefined>();
  useEffect(() => {
    if (typeof visible === 'boolean') updateToc(sectionName, visible);
  }, [visible, updateToc]);

  return (
    <RenderIfChildren
      as={PropGroup}
      label="occurrenceDetails.groups.geologicalContext"
      id={sectionName}
      onChange={(newState) => {
        if (newState !== visible) setVisible(newState);
      }}
    >
      <PlainTextField term={termMap.geologicalContextID} showDetails={showAll} />
      <PlainTextField term={termMap.earliestEonOrLowestEonothem} showDetails={showAll} />
      <PlainTextField term={termMap.latestEonOrHighestEonothem} showDetails={showAll} />
      <PlainTextField term={termMap.earliestEraOrLowestErathem} showDetails={showAll} />
      <PlainTextField term={termMap.latestEraOrHighestErathem} showDetails={showAll} />
      <PlainTextField term={termMap.earliestPeriodOrLowestSystem} showDetails={showAll} />
      <PlainTextField term={termMap.latestPeriodOrHighestSystem} showDetails={showAll} />
      <PlainTextField term={termMap.earliestEpochOrLowestSeries} showDetails={showAll} />
      <PlainTextField term={termMap.latestEpochOrHighestSeries} showDetails={showAll} />
      <PlainTextField term={termMap.earliestAgeOrLowestStage} showDetails={showAll} />
      <PlainTextField term={termMap.latestAgeOrHighestStage} showDetails={showAll} />
      <PlainTextField term={termMap.lowestBiostratigraphicZone} showDetails={showAll} />
      <PlainTextField term={termMap.highestBiostratigraphicZone} showDetails={showAll} />
      <PlainTextField term={termMap.lithostratigraphicTerms} showDetails={showAll} />
      <PlainTextField term={termMap.group} showDetails={showAll} />
      <PlainTextField term={termMap.formation} showDetails={showAll} />
      <PlainTextField term={termMap.member} showDetails={showAll} />
      <PlainTextField term={termMap.bed} showDetails={showAll} />
    </RenderIfChildren>
  );
}

function Identification({
  showAll,
  termMap,
  occurrence,
  updateToc = () => {},
}: {
  showAll: boolean;
  termMap: any;
  occurrence: any;
  updateToc: (id: string, visible: boolean) => void;
}) {
  const sectionName = 'identification';
  const [visible, setVisible] = useState<boolean | undefined>();
  useEffect(() => {
    if (typeof visible === 'boolean') updateToc(sectionName, visible);
  }, [visible, updateToc]);

  return (
    <RenderIfChildren
      as={PropGroup}
      label="occurrenceDetails.groups.identification"
      id="identification"
      onChange={(newState) => {
        if (newState !== visible) setVisible(newState);
      }}
    >
      <HtmlField term={termMap.identificationID} showDetails={showAll} />
      <PlainTextField term={termMap.identificationQualifier} showDetails={showAll} />
      <Property
        value={termMap?.typeStatus?.value}
        labelId="occurrenceFieldNames.typeStatus"
        formatter={(val) => <ConceptValue vocabulary="TypeStatus" name={val} />}
      />
      <PlainTextField term={termMap.identifiedBy} showDetails={showAll} />
      <IdentifiedById {...{ showAll, termMap, occurrence }} />
      <PlainTextField term={termMap.dateIdentified} showDetails={showAll} />
      <HtmlField term={termMap.identificationReferences} showDetails={showAll} />
      <PlainTextField term={termMap.identificationVerificationStatus} showDetails={showAll} />
      <HtmlField term={termMap.identificationRemarks} showDetails={showAll} />
    </RenderIfChildren>
  );
}

function Other({ showAll, termMap }: { showAll: boolean; termMap: any; occurrence: any }) {
  // no reason to test this, this group is always present since GBIF id is required
  return (
    <PropGroup label="occurrenceDetails.groups.other" id="other">
      <LicenseField term={termMap.license} getEnum={(value) => `enums.license.${value}`} />
      {/* RECORD LEVEL Actually belongs on Record card, but it seems wrong to put it first on the page, so I've moved it here along with other identifiers */}
      <DynamicProperties termMap={termMap} />
      <PlainTextField term={termMap.institutionCode} showDetails={showAll} />
      <HtmlField term={termMap.institutionID} showDetails={showAll} />
      <PlainTextField term={termMap.ownerInstitutionCode} showDetails={showAll} />
      <PlainTextField term={termMap.collectionCode} showDetails={showAll} />
      <HtmlField term={termMap.collectionID} showDetails={showAll} />
      <HtmlField term={termMap.datasetID} showDetails={showAll} />
      <PlainTextField term={termMap.datasetName} showDetails={showAll} />
      {/* END RECORD LEVEL */}

      <PlainTextField term={termMap.abstract} showDetails={showAll} />
      <PlainTextField term={termMap.accessRights} showDetails={showAll} />
      <PlainTextField term={termMap.accrualMethod} showDetails={showAll} />
      <PlainTextField term={termMap.accrualPeriodicity} showDetails={showAll} />
      <PlainTextField term={termMap.accrualPolicy} showDetails={showAll} />
      <PlainTextField term={termMap.alternative} showDetails={showAll} />
      <PlainTextField term={termMap.audience} showDetails={showAll} />
      <PlainTextField term={termMap.available} showDetails={showAll} />
      <HtmlField term={termMap.bibliographicCitation} showDetails={showAll} />
      <PlainTextField term={termMap.conformsTo} showDetails={showAll} />
      <PlainTextField term={termMap.contributor} showDetails={showAll} />
      <PlainTextField term={termMap.coverage} showDetails={showAll} />
      {/* <PlainTextField term={termMap.created} showDetails={showAll} /> */}
      <PlainTextField term={termMap.creator} showDetails={showAll} />
      <PlainTextField term={termMap.date} showDetails={showAll} />
      <PlainTextField term={termMap.dateAccepted} showDetails={showAll} />
      <PlainTextField term={termMap.dateCopyrighted} showDetails={showAll} />
      <PlainTextField term={termMap.dateSubmitted} showDetails={showAll} />
      <PlainTextField term={termMap.description} showDetails={showAll} />
      <PlainTextField term={termMap.educationLevel} showDetails={showAll} />
      <PlainTextField term={termMap.extent} showDetails={showAll} />
      <PlainTextField term={termMap.format} showDetails={showAll} />
      <PlainTextField term={termMap.hasFormat} showDetails={showAll} />
      <PlainTextField term={termMap.hasPart} showDetails={showAll} />
      <PlainTextField term={termMap.hasVersion} showDetails={showAll} />
      <HtmlField term={termMap.identifier} showDetails={showAll} />
      <PlainTextField term={termMap.instructionalMethod} showDetails={showAll} />
      <PlainTextField term={termMap.isFormatOf} showDetails={showAll} />
      <PlainTextField term={termMap.isPartOf} showDetails={showAll} />
      <HtmlField term={termMap.isReferencedBy} showDetails={showAll} />
      <HtmlField term={termMap.isReplacedBy} showDetails={showAll} />
      <HtmlField term={termMap.isRequiredBy} showDetails={showAll} />
      <HtmlField term={termMap.isVersionOf} showDetails={showAll} />
      <PlainTextField term={termMap.issued} showDetails={showAll} />
      <PlainTextField term={termMap.language} showDetails={showAll} />
      <PlainTextField term={termMap.mediator} showDetails={showAll} />
      <PlainTextField term={termMap.medium} showDetails={showAll} />
      <PlainTextField term={termMap.modified} showDetails={showAll} />
      <PlainTextField term={termMap.provenance} showDetails={showAll} />
      <HtmlField term={termMap.publisher} showDetails={showAll} />
      <HtmlField term={termMap.references} showDetails={showAll} />
      <HtmlField term={termMap.relation} showDetails={showAll} />
      <HtmlField term={termMap.replaces} showDetails={showAll} />
      <HtmlField term={termMap.requires} showDetails={showAll} />
      <PlainTextField term={termMap.rights} showDetails={showAll} />
      <PlainTextField term={termMap.rightsHolder} showDetails={showAll} />
      <HtmlField term={termMap.source} showDetails={showAll} />
      <PlainTextField term={termMap.spatial} showDetails={showAll} />
      <PlainTextField term={termMap.subject} showDetails={showAll} />
      <PlainTextField term={termMap.tableOfContents} showDetails={showAll} />
      <PlainTextField term={termMap.temporal} showDetails={showAll} />
      <PlainTextField term={termMap.title} showDetails={showAll} />
      <PlainTextField term={termMap.type} showDetails={showAll} />
      <PlainTextField term={termMap.valid} showDetails={showAll} />
      <BasicField label="occurrenceFieldNames.gbifID">{termMap?.gbifID?.value}</BasicField>
    </PropGroup>
  );
}

function Issues({ occurrence }: { occurrence: OccurrenceQuery['occurrence'] }) {
  if (!occurrence) return null;
  const nonTaxonomicIssues = occurrence?.nonTaxonomicIssues ?? [];
  const activeChecklistIssues = occurrence?.classification?.issues ?? [];
  const issues = Array.from(new Set([...nonTaxonomicIssues, ...activeChecklistIssues]));
  return (
    <Group
      label="occurrenceDetails.groups.issues"
      id="issues"
      description={
        <>
          <FormattedMessage id="occurrenceDetails.issuesHelpText" />{' '}
          <a
            href="https://techdocs.gbif.org/en/data-use/occurrence-issues-and-flags"
            target="_blank"
            rel="noopener noreferrer"
            className="g-underline"
          >
            <FormattedMessage id="occurrenceDetails.issuesLearnMore" />
          </a>
        </>
      }
    >
      {(issues?.length ?? 0) === 0 ? (
        <div>
          <FormattedMessage id="occurrenceDetails.issues.none" />
        </div>
      ) : (
        <>
          <div className="g-w-full g-max-w-full g-overflow-auto g-pb-2">
            <table className="gbif-table-style g-text-sm">
              <thead>
                <tr>
                  <th className="g-min-w-48">
                    <FormattedMessage id="occurrenceFieldNames.issue" />
                  </th>
                  <th className="g-min-w-96">
                    <FormattedMessage id="phrases.description" />
                  </th>
                </tr>
              </thead>
              <tbody>
                {issues?.map((issue) => (
                  <tr key={issue}>
                    <td>
                      <FormattedMessage id={`enums.occurrenceIssue.${issue}`} />
                    </td>
                    <td dir="auto">
                      <FormattedMessage id={`enums.occurrenceIssueDescription.${issue}`} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Group>
  );
}

function Citation({ occurrence }: { occurrence: OccurrenceQuery['occurrence'] }) {
  if (!occurrence) return null;
  return (
    <Group label="phrases.citation" id="citation" className="gbif-test-background">
      <TestSiteAlert className="g-mb-4" />
      <Properties breakpoint={800} className="[&>dt]:g-w-52">
        <BasicField label="phrases.citeAs">
          <div dir="ltr">
            {occurrence?.dataset?.citation?.text} https://gbif.org/occurrence/{occurrence.key}
          </div>
        </BasicField>
      </Properties>
    </Group>
  );
}

function Debug({ occurrence }: { occurrence: OccurrenceQuery['occurrence'] }) {
  if (!occurrence) return null;
  return (
    <div className="g-mb-4 g-text-sm g-scroll-mt-24" id="provenance">
      <CardContent>
        <Properties breakpoint={800} className="[&>dt]:g-w-52">
          <BasicField label="phrases.apiAccess">
            <BulletList>
              <li>
                <a
                  className="g-text-inherit g-underline"
                  href={`${import.meta.env.PUBLIC_API_V1}/occurrence/${occurrence.key}`}
                >
                  <FormattedMessage id="occurrenceDetails.processed" />
                </a>
              </li>
              <li>
                <a
                  className="g-text-inherit g-underline"
                  href={`${import.meta.env.PUBLIC_API_V1}/occurrence/${occurrence.key}/fragment`}
                >
                  <FormattedMessage id="occurrenceDetails.fragment" />
                </a>
              </li>
            </BulletList>
          </BasicField>
          <BasicField label="occurrenceDetails.dataset">
            <DynamicLink
              to={`/dataset/${occurrence.datasetKey}`}
              pageId="datasetKey"
              variables={{ key: occurrence.datasetKey ?? '' }}
              className="g-text-inherit g-underline"
            >
              {occurrence.datasetTitle}
            </DynamicLink>
          </BasicField>

          <BasicField label="occurrenceFieldNames.publisher">
            <DynamicLink
              to={`/publisher/${occurrence.publishingOrgKey}`}
              pageId="publisherKey"
              variables={{ key: occurrence.publishingOrgKey ?? '' }}
              className="g-text-inherit g-underline"
            >
              {occurrence.publisherTitle}
            </DynamicLink>
          </BasicField>
          <BasicField label="phrases.lastCrawled">{occurrence?.lastCrawled}</BasicField>
        </Properties>
      </CardContent>
    </div>
  );
}
